import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  InputAdornment,
  TextFieldProps,
} from "@mui/material";
import { Custom } from "components";
import { useTheme } from "contexts";
import { StringOrOptionType } from "types";

// icons
import { SearchRounded } from "@mui/icons-material";

type Props = TextFieldProps & {
  hasError?: boolean;
  autoReset?: boolean;
  options: StringOrOptionType[];
  onOptionSelect: (selected: StringOrOptionType) => void;
  optionLimit?: number; // New prop to limit the number of options
};

export const Search = ({
  hasError = false,
  autoReset = false,
  options,
  onOptionSelect,
  optionLimit = 20, // Default to 5 if no limit is passed
  ...props
}: Props) => {
  const { theme } = useTheme();

  const castedValue = (props.value as StringOrOptionType) || "";

  const [inputValue, setInputValue] = useState<string>("");
  const [current, setCurrent] = useState<StringOrOptionType | "">(castedValue);
  const [filteredOptions, setFilteredOptions] =
    useState<StringOrOptionType[]>(options); // Store the filtered list

  useEffect(() => {
    setCurrent(castedValue);
  }, [castedValue]);

  useEffect(() => {
    setInputValue(current as string);
  }, [current]);

  useEffect(() => {
    // Filter the options based on the input value
    if (inputValue) {
      const filtered = options.filter((option) =>
        (typeof option === "string" ? option : option.label)
          .toLowerCase()
          .includes(inputValue.toLowerCase())
      );
      setFilteredOptions(filtered.slice(0, optionLimit)); // Limit based on the optionLimit
    } else {
      // Reset to the full list when input is cleared
      setFilteredOptions(options.slice(0, optionLimit));
    }
  }, [inputValue, options, optionLimit]);

  const handleSelection = (_: any, newValue: StringOrOptionType | null) => {
    if (newValue) {
      onOptionSelect(newValue);
      autoReset && setInputValue("");
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === "Tab") {
      autoReset && setCurrent("");
    }
  };

  return (
    <Box width={1}>
      <Autocomplete
        value={current}
        options={filteredOptions} // Use the filtered options here
        getOptionKey={(option) =>
          typeof option === "string" ? option : option.value
        }
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.label
        }
        isOptionEqualToValue={(option, val) =>
          typeof option === "string"
            ? option === val
            : typeof val === "string"
              ? option.label === val
              : option.label === val.label
        }
        onChange={handleSelection}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        onKeyDown={handleKeyPress}
        renderInput={(params) => (
          <Custom.TextField
            {...params}
            {...props}
            error={hasError}
            variant="outlined"
            value={inputValue}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ color: theme.color.font.color, right: 0 }}
                >
                  <SearchRounded />
                </InputAdornment>
              ),
            }}
            sx={{
              ".MuiOutlinedInput-root": {
                pr: `${theme.spacing.md * theme.spacing.factor}px !important`,
              },
              ".MuiOutlinedInput-notchedOutline ": {
                borderColor: theme.color.border.color,
              },
            }}
          />
        )}
      />
    </Box>
  );
};
